/**
 * Types for Trade Communications (email client-style) section
 */
import type { ActivityItem } from "@/types/activity";
import type { EmailAccountResponse } from "@/types/api/email";
import type { LimboMessage } from "@/types/api/email-limbo";

/** Selected folder in the sidebar */
export type TradeCommsFolder = { type: "inbox" } | { type: "limbo" } | { type: "account"; accountId: string; email: string; sub: "all" | "limbo" };

/** Serialize folder to URL query param value */
export function folderToParam(folder: TradeCommsFolder): string {
    if (folder.type === "inbox") return "inbox";
    if (folder.type === "limbo") return "limbo";
    return `account:${folder.accountId}:${folder.sub}`;
}

/** Parse folder from URL query param; returns default inbox if invalid or account not found */
export function paramToFolder(param: string | null, accounts: EmailAccountResponse[]): TradeCommsFolder {
    if (!param) return { type: "inbox" };
    if (param === "inbox") return { type: "inbox" };
    if (param === "limbo") return { type: "limbo" };
    const match = param.match(/^account:([^:]+):(all|limbo)$/);
    if (!match) return { type: "inbox" };
    const [, accountId, sub] = match;
    const account = accounts.find((a) => a.account_id === accountId);
    if (!account) return { type: "inbox" };
    return { type: "account", accountId, email: account.email_address, sub: sub as "all" | "limbo" };
}

/** Selected item: either an email activity or a limbo message */
export type TradeCommsSelectedItem = { type: "activity"; activity: ActivityItem } | { type: "limbo"; message: LimboMessage };

/** Get email message ID from an ActivityItem (for opening full email body) */
export function getEmailMessageIdFromActivity(item: ActivityItem): string | null {
    return item.actions?.find((a) => a.modalType === "email")?.modalId ?? null;
}

export type { ActivityItem, LimboMessage, EmailAccountResponse };
